<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor Settings</h5>
        <p class="page-sub">Configure check-in behaviour, badge and NDA options</p>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table" style="max-width:640px;margin:0 auto;">
      <div v-for="n in 5" :key="n" class="sk-row"></div>
    </div>

    <div v-else class="form-card" style="max-width:640px;margin:0 auto;">

      <!-- Check-In Behaviour -->
      <div class="section-label">Check-In Behaviour</div>

      <div class="setting-row">
        <div class="toggle-info">
          <span class="toggle-name">Notify Host on Visitor Arrival</span>
          <span class="toggle-hint">Send an email to the host when their visitor checks in</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.notify_host_on_arrival" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <div class="setting-row" style="margin-top:16px;">
        <div class="toggle-info">
          <span class="toggle-name">Require Visitor Photo</span>
          <span class="toggle-hint">Make photo capture mandatory during check-in and on the kiosk</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.require_photo" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <div class="form-row" style="margin-top:16px;">
        <label class="form-label">No-Show Grace Period (minutes)</label>
        <input v-model.number="form.no_show_grace_minutes" type="number" min="0" max="1440" class="form-input" style="max-width:160px;" />
        <span class="form-hint">After this many minutes past the scheduled time, the visit is automatically marked as No Show.</span>
      </div>

      <div class="form-row" style="margin-top:16px;">
        <label class="form-label">Auto Check-Out After (hours)</label>
        <input v-model.number="form.auto_checkout_hours" type="number" min="1" max="24" class="form-input" style="max-width:160px;" placeholder="Disabled" />
        <span class="form-hint">Leave blank to require manual check-out. Set a number to auto-checkout after that many hours.</span>
      </div>

      <hr class="section-divider" />

      <!-- Kiosk -->
      <div class="section-label">Self-Service Kiosk</div>

      <div class="setting-row">
        <div class="toggle-info">
          <span class="toggle-name">Enable Public Kiosk Check-In</span>
          <span class="toggle-hint">Allow visitors to self-check-in using the public kiosk link</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.kiosk_enabled" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <div v-if="kioskUrl" class="kiosk-url-row" style="margin-top:16px;">
        <div class="form-label" style="margin-bottom:6px;">Kiosk URL</div>
        <div class="kiosk-url-wrap">
          <input readonly :value="kioskUrl" class="form-input kiosk-url-input" @click="$event.target.select()" />
          <button class="btn-copy-sm" :class="{ 'btn-copy-sm--copied': kioskUrlCopied }" @click="copyKioskUrl">
            <font-awesome-icon :icon="kioskUrlCopied ? 'check' : 'copy'" />
            {{ kioskUrlCopied ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <span class="form-hint">Share this link with visitors or display it as a QR code at reception.</span>
      </div>

      <hr class="section-divider" />

      <!-- Badge -->
      <div class="section-label">Visitor Badge</div>
      <div class="form-row">
        <label class="form-label">Badge Footer Text</label>
        <input v-model="form.badge_footer" class="form-input" maxlength="200" placeholder="e.g. All visitors must be escorted at all times." />
      </div>

      <hr class="section-divider" />

      <!-- NDA -->
      <div class="section-label">Visitor NDA / Agreement</div>

      <div class="setting-row">
        <div class="toggle-info">
          <span class="toggle-name">Require NDA Acceptance</span>
          <span class="toggle-hint">Display a non-disclosure agreement to visitors during check-in</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.nda_enabled" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <div v-if="form.nda_enabled" class="form-row" style="margin-top:16px;">
        <label class="form-label">NDA Text</label>
        <textarea v-model="form.nda_text" class="form-input" rows="5" style="resize:vertical;line-height:1.5;" placeholder="Enter the NDA or visitor agreement text…"></textarea>
        <span class="form-hint">For versioned, legally-traceable NDA management use the <strong>Documents</strong> section instead.</span>
      </div>

      <hr class="section-divider" />

      <!-- Privacy Notice -->
      <div class="section-label">Privacy &amp; Consent</div>

      <div class="setting-row">
        <div class="toggle-info">
          <span class="toggle-name">Show Privacy Notice on Kiosk</span>
          <span class="toggle-hint">Display a GDPR/NDPA privacy notice before visitors enter their details</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.privacy_notice_enabled" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <div v-if="form.privacy_notice_enabled" class="form-row" style="margin-top:16px;">
        <label class="form-label">Privacy Notice Text</label>
        <textarea v-model="form.privacy_notice_text" class="form-input" rows="4" style="resize:vertical;line-height:1.5;" placeholder="We collect your name and contact details to manage your visit. Your data will be retained for 90 days."></textarea>
      </div>

      <hr class="section-divider" />

      <!-- Kiosk Branding -->
      <div class="section-label">Kiosk Branding</div>

      <div class="form-grid-2">
        <div class="form-row">
          <label class="form-label">Brand Colour</label>
          <div style="display:flex;align-items:center;gap:10px;">
            <input type="color" v-model="form.kiosk_brand_colour" style="width:44px;height:36px;border:1px solid #dde5f0;border-radius:8px;padding:2px;cursor:pointer;" />
            <input v-model="form.kiosk_brand_colour" class="form-input" style="max-width:120px;font-size:12px;" placeholder="#004AAD" />
          </div>
          <span class="form-hint">Colour used for kiosk buttons and highlights.</span>
        </div>
        <div class="form-row">
          <label class="form-label">Kiosk Language</label>
          <select v-model="form.kiosk_language" class="form-input">
            <option value="en">English</option>
            <option value="fr">Français</option>
            <option value="es">Español</option>
            <option value="ar">العربية</option>
          </select>
          <span class="form-hint">Default language shown on the self-service kiosk.</span>
        </div>
      </div>

      <hr class="section-divider" />

      <!-- Alerts -->
      <div class="section-label">Alerts &amp; Notifications</div>

      <div class="form-row">
        <label class="form-label">Host Not-Responding Alert (minutes)</label>
        <input v-model.number="form.host_not_responding_minutes" type="number" min="5" max="120" class="form-input" style="max-width:160px;" />
        <span class="form-hint">Alert reception if the host hasn't acknowledged the visitor after this many minutes.</span>
      </div>

      <div class="form-row" style="margin-top:16px;">
        <label class="form-label">Overstay Alert (minutes)</label>
        <input v-model.number="form.overstay_alert_minutes" type="number" min="15" max="480" class="form-input" style="max-width:160px;" />
        <span class="form-hint">Send an alert to the host if a visitor has been on site longer than this.</span>
      </div>

      <div style="display:flex;justify-content:flex-end;margin-top:24px;">
        <button class="btn-primary-action" @click="save" :disabled="saving">
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          {{ saving ? 'Saving…' : 'Save Settings' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorSettings',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true, saving: false, kioskUrlCopied: false,
      form: {
        notify_host_on_arrival: true,
        no_show_grace_minutes: 60,
        auto_checkout_hours: null,
        require_photo: false,
        kiosk_enabled: true,
        nda_enabled: false,
        nda_text: '',
        badge_footer: '',
        kiosk_brand_colour: '#004AAD',
        kiosk_language: 'en',
        privacy_notice_enabled: false,
        privacy_notice_text: '',
        host_not_responding_minutes: 15,
        overstay_alert_minutes: 30,
      },
    }
  },
  computed: {
    kioskUrl() {
      const code  = this.$route.params.companyCode
      const bizId = this.$store.state.getLoggedInUser?.business_id
      if (!code || !bizId) return null
      return `${window.location.origin}/visitor-checkin/${code}/${bizId}`
    },
  },
  created() { this.load() },
  methods: {
    async load() {
      try {
        const r = await axios.get('visitors/settings')
        const s = r.data.data
        this.form = {
          notify_host_on_arrival:      s.notify_host_on_arrival,
          no_show_grace_minutes:       s.no_show_grace_minutes,
          auto_checkout_hours:         s.auto_checkout_hours,
          require_photo:               s.require_photo  ?? false,
          kiosk_enabled:               s.kiosk_enabled  ?? true,
          nda_enabled:                 s.nda_enabled,
          nda_text:                    s.nda_text ?? '',
          badge_footer:                s.badge_footer ?? '',
          kiosk_brand_colour:          s.kiosk_brand_colour ?? '#004AAD',
          kiosk_language:              s.kiosk_language ?? 'en',
          privacy_notice_enabled:      s.privacy_notice_enabled ?? false,
          privacy_notice_text:         s.privacy_notice_text ?? '',
          host_not_responding_minutes: s.host_not_responding_minutes ?? 15,
          overstay_alert_minutes:      s.overstay_alert_minutes ?? 30,
        }
      } catch { this.toast.error('Failed to load settings.') }
      finally { this.loading = false }
    },
    async copyKioskUrl() {
      try {
        await navigator.clipboard.writeText(this.kioskUrl)
        this.kioskUrlCopied = true
        setTimeout(() => { this.kioskUrlCopied = false }, 2500)
      } catch { this.toast.error('Could not copy link.') }
    },
    async save() {
      this.saving = true
      try {
        await axios.put('visitors/settings', this.form)
        this.toast.success('Settings saved.')
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save.')
      } finally { this.saving = false }
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }

.form-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.form-hint  { font-size:11px; color:#aab8cc; margin-top:2px; }

.section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:14px; }
.section-divider { border:none; border-top:1px solid #f0f4f8; margin:20px 0; }

.setting-row { display:flex; justify-content:space-between; align-items:center; gap:16px; padding:4px 0; }
.toggle-info { display:flex; flex-direction:column; gap:3px; }
.toggle-name { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-hint { font-size:12px; color:#7a90b0; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.form-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
@media(max-width:560px){ .form-grid-2 { grid-template-columns:1fr; } }
.kiosk-url-wrap { display:flex; gap:8px; align-items:center; }
.kiosk-url-input { flex:1; font-size:12px; color:#4a5e78; }
.btn-copy-sm { display:inline-flex; align-items:center; gap:5px; padding:0 14px; height:36px; border-radius:8px; border:1px solid #dde5f0; background:#f4f7fb; font-size:12px; font-weight:600; color:#3d5068; cursor:pointer; white-space:nowrap; flex-shrink:0; }
.btn-copy-sm:hover { background:#e8eef8; }
.btn-copy-sm--copied { background:#ecfdf5; border-color:#bbf7d0; color:#16a34a; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
